/**
 * A caixa de lançamento de Residências, para criar e para editar.
 *
 * Papel: reproduz o formulário genérico de lançamento da origem sobre o
 * `Dialog` — o título, Data, Residência, Descrição, Categoria, Valor, Status e
 * Observações, e os botões "Cancelar" e "Salvar lançamento" — e grava pela
 * `useSalvarLancamentoResidencia`.
 *
 * Como usar: quem monta põe o componente na tela só enquanto a caixa está
 * aberta, com uma `key` diferente por lançamento, e o tira dela em `onSalvo` e
 * em `onCancelar`; a caixa não guarda se está aberta. Sem a `key`, o React
 * reaproveitaria os campos de um lançamento no seguinte.
 *
 * A caixa só fecha por "Cancelar" e pelo salvar bem-sucedido, como na origem:
 * `Esc`, clique no véu e foco fora dela são impedidos no `DialogContent`. Ela
 * abre com o foco na própria caixa, e não num campo, para não nascer com a
 * borda de foco que a origem não mostra; o primeiro Tab chega à data, e o foco
 * continua preso dentro dela.
 *
 * Não há `<form>`: Enter num campo não envia, e os dois botões são
 * `type="button"`. O `react-hook-form` só registra os campos nativos e lê os
 * valores no clique, sem `resolver` e sem mensagem sob o campo — a origem avisa
 * tudo por `alert()`, e a falha do Zod também sai assim, com a primeira
 * mensagem.
 *
 * As três escolhas são `<select>` nativos, sem `w-full`, e a grade é
 * `1fr 1fr`, e não `grid-cols-2`: o `<select>` de residência tem a largura da
 * opção mais longa e alarga a segunda coluna, como na origem. É a exceção ao
 * que os outros formulários fazem, registrada em `docs/identidade-visual.md`.
 */
import {
  CATEGORIAS_LANCAMENTO_RESIDENCIA,
  type CategoriaLancamentoResidencia,
  type LancamentoResidencia,
  STATUS_LANCAMENTO_RESIDENCIA,
  type StatusLancamentoResidencia,
  novoLancamentoResidenciaSchema,
} from '@concierge/shared';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';
import { hojeEmIso } from '@/shared/lib/vencimento';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  type Bloco,
  type CasasSeparadas,
  opcoesDeResidencia,
  residenciaDoLancamentoNovo,
} from '../casas';
import { useSalvarLancamentoResidencia } from '../hooks/usar-residencias';
import { ROTULOS_CATEGORIA, ROTULOS_STATUS } from '../rotulos';

interface FormularioLancamentoResidenciaProps {
  /** O lançamento a editar, ou `null` para criar um novo. */
  lancamento: LancamentoResidencia | null;
  /** O bloco onde "+ Novo lançamento" foi clicado; só é lido no lançamento novo. */
  bloco: Bloco;
  /** As casas de topo já separadas, de onde saem as opções de residência. */
  casas: CasasSeparadas;
  /** Chamado depois de gravar e de a listagem ser lida de novo. */
  onSalvo: () => void;
  /** Chamado ao clicar em "Cancelar". */
  onCancelar: () => void;
}

/** Os campos como o formulário os guarda: texto, como os campos nativos devolvem. */
interface CamposDoLancamento {
  data: string;
  /** O `id` da casa, ou `''` para "Ambas / não se aplica". */
  residencia: string;
  descricao: string;
  categoria: CategoriaLancamentoResidencia;
  valor: string;
  status: StatusLancamentoResidencia;
  observacao: string;
}

/** As classes do `Input` sem `w-full`, para o `<select>` ter a largura da opção mais longa. */
const CLASSES_DO_SELECT =
  'rounded-controle border border-linha-forte bg-background px-3 py-2.5 text-sm text-foreground outline-none focus-visible:border-brass';

/**
 * O que os campos de texto e número da caixa mudam no `Input` para chegar às
 * medidas da origem: a entrelinha `normal` do campo nativo e o cinza e a opacidade
 * de exemplo do navegador.
 */
const CLASSES_DO_CAMPO =
  'leading-[normal] placeholder:[color:revert]! placeholder:[opacity:revert]!';

/** O mesmo do `CLASSES_DO_CAMPO`, com o miolo do campo de data como o do navegador. */
const CLASSES_DO_CAMPO_DE_DATA = `${CLASSES_DO_CAMPO} [&::-webkit-date-and-time-value]:[all:revert] [&::-webkit-datetime-edit]:[all:revert] [&::-webkit-datetime-edit-fields-wrapper]:[all:revert] [&::-webkit-datetime-edit-year-field]:[all:revert] [&::-webkit-datetime-edit-month-field]:[all:revert] [&::-webkit-datetime-edit-day-field]:[all:revert] [&::-webkit-calendar-picker-indicator]:[all:revert]`;

/** O grupo de rótulo e campo, com 6px entre os dois e o campo esticado na coluna. */
const CLASSES_DO_GRUPO = 'flex flex-col gap-1.5';

/** O grupo que ocupa a linha inteira da grade. */
const CLASSES_DO_GRUPO_INTEIRO = 'col-span-full flex flex-col gap-1.5';

/**
 * A entrelinha do rótulo da origem, que herda o 1,6 do `body`; o `Label` é
 * `leading-none`, e sem isto cada rótulo ficaria uns 6px mais baixo.
 */
const CLASSES_DO_ROTULO = 'leading-[1.6]';

/** O que o `alert()` diz em qualquer falha ao gravar que não seja um 409. */
const FALHA_AO_SALVAR = 'Não foi possível salvar o lançamento.';

/**
 * Os valores com que os campos abrem.
 *
 * No lançamento novo, a data é a de hoje no fuso de quem abre, por `hojeEmIso`,
 * e não o UTC, que depois das 21h daria amanhã.
 *
 * @param lancamento - O lançamento a editar, ou `null` para o novo.
 * @param bloco - O bloco de onde o lançamento novo foi aberto.
 * @returns Os campos preenchidos, com nulo trocado por texto vazio e o valor
 *   gravado escrito por `String`, que mostra zero como `0`.
 */
function valoresIniciais(
  lancamento: LancamentoResidencia | null,
  bloco: Bloco,
): CamposDoLancamento {
  if (lancamento === null) {
    return {
      data: hojeEmIso(),
      residencia: residenciaDoLancamentoNovo(bloco) ?? '',
      descricao: '',
      categoria: CATEGORIAS_LANCAMENTO_RESIDENCIA[0],
      valor: '',
      status: 'pendente',
      observacao: '',
    };
  }

  return {
    data: lancamento.data,
    residencia: lancamento.residencia?.id ?? '',
    descricao: lancamento.descricao,
    categoria: lancamento.categoria,
    valor: String(lancamento.valor),
    status: lancamento.status,
    observacao: lancamento.observacao ?? '',
  };
}

/**
 * Impede o Radix de fechar a caixa por `Esc` ou por toque e foco fora dela.
 *
 * @param evento - O evento que o Radix entregaria ao fechamento.
 */
function handleImpedirFechamento(evento: Event) {
  evento.preventDefault();
}

/**
 * Põe o foco na própria caixa ao abrir, em vez do primeiro campo.
 *
 * @param evento - O evento de foco inicial do Radix, disparado na caixa.
 */
function handleAbrir(evento: Event) {
  evento.preventDefault();
  if (evento.currentTarget instanceof HTMLElement) evento.currentTarget.focus();
}

/**
 * A caixa de lançamento.
 *
 * @param lancamento - O lançamento a editar, ou `null` para criar um novo.
 * @param bloco - O bloco de onde o lançamento novo foi aberto, que dá a casa
 *   inicial por `residenciaDoLancamentoNovo`.
 * @param casas - As casas de topo separadas, de onde saem as opções.
 * @param onSalvo - Chamado depois de gravar e de a listagem ser lida de novo.
 * @param onCancelar - Chamado ao clicar em "Cancelar".
 * @returns O `Dialog` aberto, com os campos e os dois botões.
 */
export function FormularioLancamentoResidencia({
  lancamento,
  bloco,
  casas,
  onSalvo,
  onCancelar,
}: FormularioLancamentoResidenciaProps) {
  const { register, getValues, setValue } = useForm<CamposDoLancamento>({
    defaultValues: valoresIniciais(lancamento, bloco),
  });
  const salvar = useSalvarLancamentoResidencia();
  // "Cancelar" segue ativo durante a gravação; se ela volta depois da caixa
  // desmontada, `onSalvo` fecharia a caixa que a pessoa abriu depois.
  const montada = useRef(true);

  useEffect(() => {
    montada.current = true;
    return () => {
      montada.current = false;
    };
  }, []);

  const opcoes = opcoesDeResidencia(casas, lancamento?.residencia ?? null);

  /**
   * Confere, monta e grava o lançamento, na ordem do salvar da origem.
   *
   * Data e descrição faltando param tudo num `alert()`; o valor é lido por
   * `parseFloat`, que é o que o campo numérico da origem entrega, e o vazio e o
   * ilegível viram zero. Depois do Zod, a gravação; deu errado, a caixa fica
   * aberta com o que estava digitado.
   */
  async function handleSalvar() {
    const valores = getValues();
    const data = valores.data.trim();
    const descricao = valores.descricao.trim();

    if (data === '' || descricao === '') {
      window.alert('Preencha ao menos a data e a descrição.');
      return;
    }

    const corpo = novoLancamentoResidenciaSchema.safeParse({
      data,
      residenciaId: valores.residencia === '' ? null : valores.residencia,
      descricao,
      categoria: valores.categoria,
      valor: Number.parseFloat(valores.valor) || 0,
      status: valores.status,
      observacao: valores.observacao.trim(),
    });

    if (!corpo.success) {
      window.alert(corpo.error.issues[0]?.message ?? FALHA_AO_SALVAR);
      return;
    }

    try {
      await salvar.mutateAsync({ id: lancamento?.id ?? null, dados: corpo.data });
      if (montada.current) onSalvo();
    } catch (erro) {
      const conflito = erro instanceof ErroDaApi && erro.status === 409;
      // A casa escolhida mudou ou sumiu: o campo volta a "Ambas" e o valor lido
      // no próximo clique deixa de ser o `id` antigo.
      if (conflito) setValue('residencia', '');
      window.alert(conflito ? erro.message : FALHA_AO_SALVAR);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(aberta) => {
        if (!aberta) onCancelar();
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        onOpenAutoFocus={handleAbrir}
        onEscapeKeyDown={handleImpedirFechamento}
        onPointerDownOutside={handleImpedirFechamento}
        onInteractOutside={handleImpedirFechamento}
      >
        <DialogHeader>
          <DialogTitle>
            {lancamento === null ? 'Novo' : 'Editar'} lançamento — Residências
          </DialogTitle>
        </DialogHeader>

        <div className="grid [grid-template-columns:1fr_1fr] gap-4">
          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="lancamento-residencia-data" className={CLASSES_DO_ROTULO}>
              DATA
            </Label>
            <Input
              id="lancamento-residencia-data"
              type="date"
              className={CLASSES_DO_CAMPO_DE_DATA}
              {...register('data')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="lancamento-residencia-casa" className={CLASSES_DO_ROTULO}>
              RESIDÊNCIA
            </Label>
            <select
              id="lancamento-residencia-casa"
              className={CLASSES_DO_SELECT}
              {...register('residencia')}
            >
              {opcoes.map((opcao) => (
                <option key={opcao.valor} value={opcao.valor}>
                  {opcao.rotulo}
                </option>
              ))}
            </select>
          </div>

          <div className={CLASSES_DO_GRUPO_INTEIRO}>
            <Label htmlFor="lancamento-residencia-descricao" className={CLASSES_DO_ROTULO}>
              DESCRIÇÃO
            </Label>
            <Input
              id="lancamento-residencia-descricao"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Ex: Revisão do carro"
              {...register('descricao')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="lancamento-residencia-categoria" className={CLASSES_DO_ROTULO}>
              CATEGORIA
            </Label>
            <select
              id="lancamento-residencia-categoria"
              className={CLASSES_DO_SELECT}
              {...register('categoria')}
            >
              {CATEGORIAS_LANCAMENTO_RESIDENCIA.map((categoria) => (
                <option key={categoria} value={categoria}>
                  {ROTULOS_CATEGORIA[categoria]}
                </option>
              ))}
            </select>
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="lancamento-residencia-valor" className={CLASSES_DO_ROTULO}>
              VALOR (R$)
            </Label>
            <Input
              id="lancamento-residencia-valor"
              type="number"
              className={CLASSES_DO_CAMPO}
              step="0.01"
              placeholder="0,00"
              {...register('valor')}
            />
          </div>

          <div className={CLASSES_DO_GRUPO}>
            <Label htmlFor="lancamento-residencia-status" className={CLASSES_DO_ROTULO}>
              STATUS
            </Label>
            <select
              id="lancamento-residencia-status"
              className={CLASSES_DO_SELECT}
              {...register('status')}
            >
              {STATUS_LANCAMENTO_RESIDENCIA.map((status) => (
                <option key={status} value={status}>
                  {ROTULOS_STATUS[status]}
                </option>
              ))}
            </select>
          </div>

          <div className={CLASSES_DO_GRUPO_INTEIRO}>
            <Label htmlFor="lancamento-residencia-observacao" className={CLASSES_DO_ROTULO}>
              OBSERVAÇÕES
            </Label>
            <Input
              id="lancamento-residencia-observacao"
              type="text"
              className={CLASSES_DO_CAMPO}
              placeholder="Notas adicionais"
              {...register('observacao')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="text-sm/[normal]"
            onClick={onCancelar}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            className="text-sm/[normal]"
            onClick={() => void handleSalvar()}
            disabled={salvar.isPending}
          >
            Salvar lançamento
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
